import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { TimePicker } from "../components/ui/time-picker";
import { Clock, Plus, Trash2, Edit, Copy, Palette, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Los días de la semana se traducirán usando la función t() dentro del componente
const DAYS_OF_WEEK = [
  { value: "monday", translationKey: "pages.workSchedules.monday" },
  { value: "tuesday", translationKey: "pages.workSchedules.tuesday" },
  { value: "wednesday", translationKey: "pages.workSchedules.wednesday" },
  { value: "thursday", translationKey: "pages.workSchedules.thursday" },
  { value: "friday", translationKey: "pages.workSchedules.friday" },
  { value: "saturday", translationKey: "pages.workSchedules.saturday" },
  { value: "sunday", translationKey: "pages.workSchedules.sunday" },
];

const DEFAULT_COLORS = [
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#06B6D4", // Cyan
  "#84CC16", // Lime
];

export default function WorkSchedules() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const { user, canManageCompanies } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    color: DEFAULT_COLORS[0],
    company_id: "",
    days: DAYS_OF_WEEK.map(day => ({
      day_of_week: day.value,
      start_time: "",
      end_time: "",
      is_active: false,
    })),
    is_active: true,
  });

  const canSelectCompany = () => canManageCompanies();

  useEffect(() => {
    fetchSchedules();
    fetchCompanies();
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/companies`, {
        headers: getAuthHeaders(),
      });
      setCompanies(response.data);
    } catch (error) {
      console.error("Error al cargar compañías:", error);
    }
  };

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompany !== "all") params.append("company_id", selectedCompany);

      const response = await axios.get(`${API_URL}/work-schedules?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setSchedules(response.data);
    } catch (error) {
      console.error("Error al cargar jornadas:", error);
      toast.error(t("pages.workSchedules.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingSchedule(null);
    setFormData({
      name: "",
      color: DEFAULT_COLORS[0],
      company_id: canSelectCompany() ? (selectedCompany !== "all" ? selectedCompany : "") : user.company_id,
      days: DAYS_OF_WEEK.map(day => ({
        day_of_week: day.value,
        start_time: "",
        end_time: "",
        is_active: false,
      })),
      is_active: true,
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (schedule) => {
    setEditingSchedule(schedule);
    
    // Crear un mapa de días existentes
    const daysMap = new Map();
    schedule.days.forEach(day => {
      daysMap.set(day.day_of_week, day);
    });
    
    // Crear el array de días con los valores existentes o vacíos
    const days = DAYS_OF_WEEK.map(day => {
      const existing = daysMap.get(day.value);
      return existing || {
        day_of_week: day.value,
        start_time: "",
        end_time: "",
        is_active: false,
      };
    });
    
    setFormData({
      name: schedule.name || "",
      color: schedule.color || DEFAULT_COLORS[0],
      company_id: schedule.company_id,
      days: days,
      is_active: schedule.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleDayChange = (dayIndex, field, value) => {
    const newDays = [...formData.days];
    newDays[dayIndex] = {
      ...newDays[dayIndex],
      [field]: value,
    };
    setFormData({ ...formData, days: newDays });
  };

  const duplicateDay = (sourceIndex) => {
    if (sourceIndex === DAYS_OF_WEEK.length - 1) {
      toast.error(t("pages.workSchedules.noNextDay"));
      return;
    }
    
    const sourceDay = formData.days[sourceIndex];
    const newDays = [...formData.days];
    newDays[sourceIndex + 1] = {
      ...newDays[sourceIndex + 1],
      start_time: sourceDay.start_time,
      end_time: sourceDay.end_time,
      is_active: sourceDay.is_active,
    };
    setFormData({ ...formData, days: newDays });
    toast.success(t("pages.workSchedules.dayDuplicated"));
  };

  const handleSave = async () => {
    // Filtrar solo los días que tienen horario configurado
    const activeDays = formData.days.filter(day => 
      day.start_time && day.end_time && day.is_active
    );

    if (activeDays.length === 0) {
      toast.error(t("pages.workSchedules.atLeastOneDay"));
      return;
    }

    // Validar formato de hora
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    for (const day of activeDays) {
      if (!timeRegex.test(day.start_time) || !timeRegex.test(day.end_time)) {
        toast.error(t("pages.workSchedules.invalidTimeFormat"));
        return;
      }
    }

    try {
      const payload = {
        name: formData.name || null,
        color: formData.color || null,
        company_id: formData.company_id || null,
        days: activeDays,
        is_active: formData.is_active,
      };

      if (editingSchedule) {
        await axios.put(
          `${API_URL}/work-schedules/${editingSchedule.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success(t("pages.workSchedules.scheduleUpdated"));
      } else {
        await axios.post(`${API_URL}/work-schedules`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success(t("pages.workSchedules.scheduleCreated"));
      }

      setIsDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      console.error("Error al guardar jornada:", error);
      toast.error(getErrorMessage(error, "api.errors.saveError"));
    }
  };

  const handleDuplicate = async (schedule) => {
    try {
      await axios.post(
        `${API_URL}/work-schedules/${schedule.id}/duplicate`,
        {},
        {
          headers: getAuthHeaders(),
        }
      );
      toast.success(t("pages.workSchedules.scheduleDuplicated"));
      fetchSchedules();
    } catch (error) {
      console.error("Error al duplicar jornada:", error);
      toast.error(getErrorMessage(error, "api.errors.duplicateError"));
    }
  };

  const handleDeleteClick = (schedule) => {
    setScheduleToDelete(schedule);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!scheduleToDelete) return;

    try {
      await axios.delete(`${API_URL}/work-schedules/${scheduleToDelete.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(t("pages.workSchedules.scheduleDeleted"));
      setIsDeleteDialogOpen(false);
      setScheduleToDelete(null);
      fetchSchedules();
    } catch (error) {
      console.error("Error al eliminar jornada:", error);
      toast.error(getErrorMessage(error, "api.errors.deleteError"));
    }
  };

  const getDayLabel = (day) => {
    const dayObj = DAYS_OF_WEEK.find(d => d.value === day);
    return dayObj ? t(dayObj.translationKey) : day;
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : companyId;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.workSchedules.title")}
          </h1>
          <p className="text-slate-600 mt-1">{t("pages.workSchedules.subtitle")}</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-schedule-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t("pages.workSchedules.newSchedule")}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            {canSelectCompany() && (
              <div className="w-full sm:w-[250px]">
                <Label className="text-sm text-slate-600 mb-1 block">
                  {t("pages.workSchedules.filterByCompany")}
                </Label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("pages.workSchedules.allCompanies")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.workSchedules.allCompanies")}</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Schedules List */}
      <div className="grid gap-6">
        {loading ? (
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            </CardContent>
          </Card>
        ) : schedules.length === 0 ? (
          <Card>
            <CardContent className="p-8">
              <div className="text-center text-slate-500">
                {t("pages.workSchedules.noSchedules")}
              </div>
            </CardContent>
          </Card>
        ) : (
          schedules.map((schedule) => (
            <Card key={schedule.id} className="overflow-hidden">
              <CardHeader 
                className="pb-3"
                style={{
                  borderLeft: schedule.color ? `4px solid ${schedule.color}` : "4px solid #3B82F6"
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {schedule.color && (
                      <div
                        className="w-4 h-4 rounded-full border-2 border-slate-300"
                        style={{ backgroundColor: schedule.color }}
                      />
                    )}
                    <div>
                      <CardTitle>
                        {schedule.name || t("pages.workSchedules.defaultScheduleName")}
                      </CardTitle>
                      {canSelectCompany() && (
                        <p className="text-sm text-slate-500 mt-1">
                          {getCompanyName(schedule.company_id)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={schedule.is_active ? "default" : "secondary"}
                      className={
                        schedule.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-600"
                      }
                    >
                      {schedule.is_active
                        ? t("pages.workSchedules.active")
                        : t("pages.workSchedules.inactive")}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDuplicate(schedule)}
                      title={t("pages.workSchedules.duplicate")}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(schedule)}
                      title={t("pages.workSchedules.edit")}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteClick(schedule)}
                      title={t("pages.workSchedules.delete")}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-2">
                  {(() => {
                    // Crear un mapa de días existentes
                    const daysMap = new Map();
                    if (schedule.days && schedule.days.length > 0) {
                      schedule.days.forEach(day => {
                        daysMap.set(day.day_of_week, day);
                      });
                    }
                    
                    // Crear array completo de los 7 días
                    const allDays = DAYS_OF_WEEK.map(day => {
                      const existing = daysMap.get(day.value);
                      return existing || {
                        day_of_week: day.value,
                        start_time: "",
                        end_time: "",
                        is_active: false,
                      };
                    });
                    
                    return allDays.map((day) => (
                      <Card key={day.day_of_week} className="border-2">
                        <CardHeader className="pb-1 pt-2 px-2">
                          <div className="flex flex-col items-center gap-1">
                            <CardTitle className="text-xs font-medium text-center">
                              {getDayLabel(day.day_of_week)}
                            </CardTitle>
                            <div className="flex items-center gap-1">
                              <Switch
                                checked={day.is_active}
                                disabled
                                className={day.is_active ? "data-[state=checked]:bg-blue-600" : ""}
                              />
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0 px-2 pb-2">
                          {day.is_active ? (
                            <div className="space-y-1 text-center">
                              <div className="text-xs font-medium">{day.start_time}</div>
                              <div className="text-xs text-slate-500">-</div>
                              <div className="text-xs font-medium">{day.end_time}</div>
                            </div>
                          ) : (
                            <div className="text-xs text-slate-400 text-center">
                              {t("pages.workSchedules.notAvailable")}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSchedule
                ? t("pages.workSchedules.editSchedule")
                : t("pages.workSchedules.newSchedule")}
            </DialogTitle>
            <DialogDescription>
              {editingSchedule
                ? t("pages.workSchedules.editScheduleDescription")
                : t("pages.workSchedules.newScheduleDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {canSelectCompany() && (
              <div className="space-y-2">
                <Label>{t("pages.companies.name")} {!editingSchedule && "*"}</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, company_id: value })
                  }
                  disabled={!editingSchedule && !formData.company_id ? false : false}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("pages.companies.selectCompany")} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("pages.workSchedules.scheduleName")} ({t("common.optional")})</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t("pages.workSchedules.scheduleNamePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("pages.workSchedules.color")} ({t("common.optional")})</Label>
                <div className="flex items-center gap-3">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="h-10 w-20 cursor-pointer"
                  />
                  <div className="flex gap-2 flex-wrap">
                    {DEFAULT_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          formData.color === color
                            ? "border-slate-900 scale-110"
                            : "border-slate-300 hover:scale-105"
                        }`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-lg font-semibold">{t("pages.workSchedules.weeklySchedule")}</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">
                    {t("pages.workSchedules.active")}
                  </Label>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {formData.days.map((day, index) => (
                  <Card key={day.day_of_week} className="border-2">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm font-medium">
                          {getDayLabel(day.day_of_week)}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500">
                            {t("pages.workSchedules.available")}
                          </Label>
                          <Switch
                            checked={day.is_active}
                            onCheckedChange={(checked) =>
                              handleDayChange(index, "is_active", checked)
                            }
                            className={day.is_active ? "data-[state=checked]:bg-blue-600" : ""}
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {day.is_active ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">
                                {t("pages.workSchedules.from")}
                              </Label>
                              <TimePicker
                                value={day.start_time}
                                onChange={(value) =>
                                  handleDayChange(index, "start_time", value)
                                }
                                className="h-9"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">
                                {t("pages.workSchedules.to")}
                              </Label>
                              <TimePicker
                                value={day.end_time}
                                onChange={(value) =>
                                  handleDayChange(index, "end_time", value)
                                }
                                className="h-9"
                              />
                            </div>
                          </div>
                          {index < DAYS_OF_WEEK.length - 1 && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full"
                              onClick={() => duplicateDay(index)}
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              {t("pages.workSchedules.duplicateToNext")}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400 text-center py-2">
                          {t("pages.workSchedules.notAvailable")}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={(open) => {
        setIsDeleteDialogOpen(open);
        if (!open) {
          setScheduleToDelete(null);
        }
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <AlertDialogTitle className="text-xl">
                {t("pages.workSchedules.deleteSchedule")}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base pt-2">
              {scheduleToDelete && (
                <>
                  <p className="mb-2">
                    {t("pages.workSchedules.confirmDeleteMessage", {
                      name: scheduleToDelete.name || t("pages.workSchedules.defaultScheduleName")
                    })}
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mt-3 border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-1">
                      {scheduleToDelete.name || t("pages.workSchedules.defaultScheduleName")}
                    </p>
                    {scheduleToDelete.days && scheduleToDelete.days.length > 0 && (
                      <p className="text-xs text-slate-500">
                        {scheduleToDelete.days.length} {t("pages.workSchedules.daysConfigured")}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-red-600 font-medium mt-3">
                    {t("pages.workSchedules.deleteWarning")}
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setScheduleToDelete(null)}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t("pages.workSchedules.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
